import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dataType } from '../utils';
import { NoteInsightsViewOptions } from '../../../Notes/NoteInsightsContextProvider';

type useMyUtilizationDataPaginationProps = {
  currentData: any[];
  dateSet: string[];
  currentPage: number;
  onNext: () => void;
  onPrevious: () => void;
  showPrev: boolean;
  showNext: boolean;
};

const sliceUtilizationData = (
  firstPageIndex: number,
  lastPageIndex: number,
  wholeData: dataType[]
) => {
  return wholeData.map((x) => {
    const existsingChartData = [...x.data];
    const slicedData = existsingChartData.slice(firstPageIndex, lastPageIndex);

    return {
      name: x.name,
      color: x.color,
      data: slicedData
    };
  });
};

export const useMyUtilizationDataPagination = (
  data: dataType[],
  selectedViewOption: NoteInsightsViewOptions,
  pageSize: number
): useMyUtilizationDataPaginationProps => {
  const [currentPage, setCurrentPage] = useState(1);
  const currentData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;
    const slicedData = sliceUtilizationData(
      firstPageIndex,
      lastPageIndex,
      data
    );
    return slicedData;
  }, [currentPage, data, pageSize]);

  const onNext = useCallback(() => {
    setCurrentPage((page) => page + 1);
  }, []);

  const onPrevious = useCallback(() => {
    setCurrentPage((page) => page - 1);
  }, []);

  const initialRender = useRef(false);

  useEffect(() => {
    if (initialRender.current) {
      setCurrentPage(1);
    } else {
      initialRender.current = true;
    }
  }, [selectedViewOption, initialRender]);

  return useMemo(() => {
    return {
      currentData,
      dateSet: (currentData[0] && currentData[0].data.map((d) => d.date)) || [],
      currentPage,
      onNext,
      onPrevious,
      showPrev: currentPage > 1,
      showNext: currentPage * pageSize < ((data[0] && data[0].data.length) || 0)
    };
  }, [currentData, currentPage, data, onNext, onPrevious, pageSize]);
};
